<template>
  <div>
    <section aria-labelledby="notes-title">
      <Card extra-classes="overflow-visible">
        <div class="divide-y divide-gray-200">
          <div class="px-4 py-5 sm:px-6 flex justify-between">
            <h2 id="notes-title" class="text-lg font-medium text-gray-900">Activity</h2>
            <div class="flex">
              <TButton
                class="ml-2"
                v-if="contact.notesAndEmails.length > notesIndex"
                label="Show All"
                color="white"
                @click="notesIndex = notesIndex + 999999"
              >
              </TButton>
              <TButton
                class="ml-2"
                v-else-if="contact.notesAndEmails.length > 5"
                label="Show Less"
                color="white"
                @click="notesIndex = 5"
              >
              </TButton>
            </div>
          </div>
          <div class="px-4 py-6 sm:px-6">
            <ul role="list" class="divide-y divide-gray-200">
              <li v-for="note of contact.notesAndEmails.slice(0, notesIndex)" :key="note.id" class="py-4">
                <div v-if="note.activityType == 'note'" class="flex space-x-3">
                  <div class="flex-shrink-0">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      class="w-6 h-6 text-gray-400"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M12 2.25c-2.429 0-4.817.178-7.152.521C2.87 3.061 1.5 4.795 1.5 6.741v6.018c0 1.946 1.37 3.68 3.348 3.97.877.129 1.761.234 2.652.316V21a.75.75 0 001.28.53l4.184-4.183a.39.39 0 01.266-.112c2.006-.05 3.982-.22 5.922-.506 1.978-.29 3.348-2.023 3.348-3.97V6.741c0-1.947-1.37-3.68-3.348-3.97A49.145 49.145 0 0012 2.25zM8.25 8.625a1.125 1.125 0 100 2.25 1.125 1.125 0 000-2.25zm2.625 1.125a1.125 1.125 0 112.25 0 1.125 1.125 0 01-2.25 0zm4.875-1.125a1.125 1.125 0 100 2.25 1.125 1.125 0 000-2.25z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </div>
                  <div>
                    <div class="text-sm">
                      <div class="font-medium text-gray-900">{{ formattedNoteTitle(note) }}</div>
                    </div>
                    <div class="mt-1 text-sm text-gray-700 user-generated-content">
                      <div v-html="sanitize(note.content)"></div>
                    </div>
                    <div class="mt-2 space-x-2 text-sm flex">
                      <span class="font-medium text-gray-500"> {{ fromNow(note.created_at) }} ago | </span>
                      <span @click="deleteNote(note.id)" class="cursor-pointer font-medium text-gray-500">delete</span>
                    </div>
                  </div>
                </div>
                <div v-if="note.activityType == 'event'" class="flex space-x-3">
                  <div class="flex-shrink-0">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      class="w-6 h-6 text-gray-400"
                    >
                      <path
                        d="M12.75 12.75a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM7.5 15.75a.75.75 0 100-1.5.75.75 0 000 1.5zM8.25 17.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM9.75 15.75a.75.75 0 100-1.5.75.75 0 000 1.5zM10.5 17.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM12 15.75a.75.75 0 100-1.5.75.75 0 000 1.5zM12.75 17.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM14.25 15.75a.75.75 0 100-1.5.75.75 0 000 1.5zM15 17.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM16.5 15.75a.75.75 0 100-1.5.75.75 0 000 1.5zM15 12.75a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM16.5 13.5a.75.75 0 100-1.5.75.75 0 000 1.5z"
                      />
                      <path
                        fill-rule="evenodd"
                        d="M6.75 2.25A.75.75 0 017.5 3v1.5h9V3A.75.75 0 0118 3v1.5h.75a3 3 0 013 3v11.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V7.5a3 3 0 013-3H6V3a.75.75 0 01.75-.75zm13.5 9a1.5 1.5 0 00-1.5-1.5H5.25a1.5 1.5 0 00-1.5 1.5v7.5a1.5 1.5 0 001.5 1.5h13.5a1.5 1.5 0 001.5-1.5v-7.5z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </div>
                  <div>
                    <div class="text-sm">
                      <div class="flex">
                        <div class="font-medium text-gray-900 mr-2">Event</div>
                        <svg
                          v-if="note.status == 'active'"
                          class="flex-shrink-0 mr-1.5 h-5 w-5 text-green-400"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clip-rule="evenodd"
                          />
                        </svg>
                        <svg
                          v-else-if="note.status == 'canceled'"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          class="flex-shrink-0 mr-1.5 h-5 w-5 text-red-400"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-1.72 6.97a.75.75 0 10-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 101.06 1.06L12 13.06l1.72 1.72a.75.75 0 101.06-1.06L13.06 12l1.72-1.72a.75.75 0 10-1.06-1.06L12 10.94l-1.72-1.72z"
                            clip-rule="evenodd"
                          />
                        </svg>
                        <span class="capitalize font-medium text-gray-500 mr-1.5">
                          {{ note.status }}
                        </span>
                      </div>
                    </div>
                    <div class="mt-1 text-sm text-gray-700">
                      <p>
                        {{ note.name }} - {{ formatDate(note.start_time, 'MMMM dd yyyy, h:mm a') }} to
                        {{ formatDate(note.end_time, 'h:mm a') }}
                      </p>
                    </div>
                    <div class="mt-2 space-x-2 text-sm">
                      <span class="font-medium text-gray-500">
                        {{ new Date(note.end_time) > new Date() ? 'in' : '' }}
                        {{ fromNow(note.end_time) }}
                        {{ new Date(note.end_time) > new Date() ? '' : 'ago' }}
                      </span>
                    </div>
                  </div>
                </div>
                <div v-if="note.activityType == 'email'" class="flex space-x-3">
                  <contact-email-history-preview :email="note" />
                </div>
              </li>
            </ul>
          </div>
        </div>
        <div class="bg-gray-50 px-4 py-6 sm:px-6">
          <div class="flex space-x-3">
            <div class="min-w-0 flex-1">
              <QuillInput name="comment" label="About" :labelSrOnly="true" size="large" v-model="contactNote.content" />
              <div class="mt-3 flex items-center justify-end gap-1">
                <SelectMenu
                  v-model="contactNote.noteType"
                  :options="['General note', 'Log a call', 'Log an email', 'Log a text']"
                  initialValue="General note"
                  @input="val => (contactNote.noteType = val)"
                />
                <TButton label="Save" color="indigo" @click="createNote()">
                  <LocalLoadingSpinner />
                </TButton>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </section>
  </div>
</template>

<script>
import Card from '../Card/index.vue';
import ContactEmailHistoryPreview from './ContactEmailHistoryPreview.vue';
import { sanitizeRichText } from '../../utilities/sanitization';
import SelectMenu from '../shared/SelectMenu.vue';
import DateUtility from '../../utilities/dates';
import QuillInput from '../shared/QuillInput.vue';

export default {
  props: ['contact'],
  components: {
    Card,
    ContactEmailHistoryPreview,
    SelectMenu,
    QuillInput,
  },
  data() {
    return {
      contactNote: {
        content: '',
        followupAt: '',
        noteType: 'general',
      },
      notesIndex: 5,
    };
  },
  methods: {
    ...DateUtility,
    sanitize(html) {
      return sanitizeRichText(html);
    },
    createNote() {
      const self = this;

      if (this.contactNote.noteType === 'General note') {
        this.contactNote.noteType = 'general';
      } else if (this.contactNote.noteType === 'Log a call') {
        this.contactNote.noteType = 'call';
      } else if (this.contactNote.noteType === 'Log an email') {
        this.contactNote.noteType = 'email';
      } else if (this.contactNote.noteType === 'Log a text') {
        this.contactNote.noteType = 'text';
      }

      self.$store.commit('general/showLocalLoadingSpinner');
      fetch('/api/contact_notes', {
        method: 'POST',
        body: JSON.stringify({
          contact_id: self.contact.id,
          content: self.contactNote.content,
          followup_at: self.contactNote.followupAt,
          note_type: self.contactNote.noteType,
        }),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': document.querySelectorAll('[name="csrf-token"]')[0].content,
        },
      })
        .then(response => {
          return response.json();
        })
        .then(data => {
          self.$emit('activityCreated');
          self.$store.commit('general/hideLocalLoadingSpinner');
          if (data.status != 'error') {
            self.contactNote.content = '';
            self.contactNote.followupAt = '';
            self.$store.commit('general/showAlert', {
              alertType: 'success',
              alertTitle: 'Note created',
              alertDescription:
                'Your candidate note was successfully created.  Use the buttons below to view your new contact, or dismiss this alert.',
            });
          } else {
            self.$store.commit('general/showAlert', {
              alertType: 'error',
              alertTitle: 'Note create error',
              alertDescription: data.message,
            });
          }
        });
    },
    deleteNote(id) {
      const self = this;
      fetch(`/api/contact_notes/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': document.querySelectorAll('[name="csrf-token"]')[0].content,
        },
      })
        .then(response => {
          return response.json();
        })
        .then(data => {
          self.$emit('activityDeleted');
          if (data.status != 'error') {
            self.$store.commit('general/showAlert', {
              alertType: 'success',
              alertTitle: 'Activity deleted',
              alertDescription:
                'Your activity was successfully deleted.  Use the buttons below to view your new contact, or dismiss this alert.',
            });
          } else {
            self.$store.commit('general/showAlert', {
              alertType: 'error',
              alertTitle: 'Activity delete error',
              alertDescription: data.message,
            });
          }
        });
    },
    formattedNoteTitle(note) {
      const t = note.note_type;
      if (t === 'general') {
        return `${note.user.email} left a note`;
      } else if (t === 'call') {
        return `${note.user.email} logged a call`;
      } else if (t === 'email') {
        return `${note.user.email} logged an email`;
      } else if (t === 'text') {
        return `${note.user.email} logged a text`;
      }
      return note.user.email;
    },
  },
};
</script>
